import React from 'react'
import styles from './index.module.scss'

// Renders the markdown subset stored in comments (see utils/commentMarkdown)
// as React elements — no innerHTML, so comment text cannot inject markup.

interface CommentMarkdownProps {
    text: string
    className?: string
}

interface ListItem {
    text: string
    children: ListBlock[]
}

interface ListBlock {
    isOrdered: boolean
    start: number
    items: ListItem[]
}

const FENCE_RE = /^\s*```/
const HEADING_RE = /^(#{1,6})\s+(.+)$/
const QUOTE_RE = /^\s*>\s?(.*)$/
const LIST_RE = /^(\s*)([-*+]|\d+[.)])\s+(.*)$/
const HR_RE = /^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/
const TABLE_ROW_RE = /^\s*\|.*\|\s*$/
const TABLE_SEPARATOR_RE = /^\s*\|?\s*:?-{3,}:?\s*(?:\|\s*:?-{3,}:?\s*)*\|?\s*$/
const INLINE_RE = /\\([\\`*_~|[\]()#>!-])|`([^`\n]+)`|\*\*(?=\S)([^\n]+?)\*\*|~~(?=\S)([^\n]+?)~~|\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"])|\*(?=[^\s*])([^*\n]+?)\*/g

const renderInline = (text: string, keyPrefix: string): React.ReactNode[] => {
    const nodes: React.ReactNode[] = []
    let lastIndex = 0
    let match: RegExpExecArray | null

    INLINE_RE.lastIndex = 0
    const matches: RegExpExecArray[] = []
    while ((match = INLINE_RE.exec(text)) !== null) matches.push(match)

    matches.forEach((current, index) => {
        if (current.index > lastIndex) nodes.push(text.slice(lastIndex, current.index))
        lastIndex = current.index + current[0].length

        const key = `${keyPrefix}-${index}`
        const [, escaped, code, bold, strike, linkText, linkUrl, bareUrl, italic] = current

        if (escaped !== undefined) nodes.push(escaped)
        else if (code !== undefined) nodes.push(<code key={key} className={styles.commentMarkdown__code}>{code}</code>)
        else if (bold !== undefined) nodes.push(<strong key={key}>{renderInline(bold, key)}</strong>)
        else if (strike !== undefined) nodes.push(<s key={key}>{renderInline(strike, key)}</s>)
        else if (linkUrl !== undefined || bareUrl !== undefined) {
            nodes.push(
                <a key={key} className={styles.commentMarkdown__link} href={linkUrl ?? bareUrl} target="_blank" rel="noopener noreferrer">
                    {linkText !== undefined ? renderInline(linkText, key) : bareUrl}
                </a>
            )
        } else nodes.push(<em key={key}>{renderInline(italic, key)}</em>)
    })

    if (lastIndex < text.length) nodes.push(text.slice(lastIndex))
    return nodes
}

// Splits on unescaped "|" (cells escape it as "\|"); the escapes are kept for
// renderInline to unescape.
const splitTableRow = (line: string) => {
    const cells: string[] = []
    let cell = ''
    const row = line.trim().replace(/^\|/, '')

    for (let index = 0; index < row.length; index++) {
        if (row[index] === '\\' && index + 1 < row.length) {
            cell += row[index] + row[++index]
        } else if (row[index] === '|') {
            cells.push(cell.trim())
            cell = ''
        } else {
            cell += row[index]
        }
    }
    if (cell.trim()) cells.push(cell.trim())

    return cells
}

const parseList = (lines: string[]) => {
    const roots: ListBlock[] = []
    const stack: { indent: number, block: ListBlock }[] = []

    lines.forEach((line) => {
        const [, rawIndent, marker, text] = line.match(LIST_RE) || []
        const indent = rawIndent.replace(/\t/g, '  ').length
        const isOrdered = /\d/.test(marker)

        while (stack.length && stack[stack.length - 1].indent > indent) stack.pop()
        const top = stack[stack.length - 1]
        if (top && top.indent === indent && top.block.isOrdered === isOrdered) {
            top.block.items.push({ text, children: [] })
            return
        }
        if (top && top.indent === indent) stack.pop()

        const block: ListBlock = { isOrdered, start: isOrdered ? parseInt(marker, 10) : 1, items: [{ text, children: [] }] }
        const parent = stack[stack.length - 1]
        if (parent) parent.block.items[parent.block.items.length - 1].children.push(block)
        else roots.push(block)
        stack.push({ indent, block })
    })

    return roots
}

const renderList = (block: ListBlock, key: string): React.ReactNode => {
    const items = block.items.map((item, index) => (
        <li key={`${key}-${index}`}>
            {renderInline(item.text, `${key}-${index}`)}
            {item.children.map((child, childIndex) => renderList(child, `${key}-${index}-${childIndex}`))}
        </li>
    ))

    return block.isOrdered
        ? <ol key={key} className={styles.commentMarkdown__list} start={block.start}>{items}</ol>
        : <ul key={key} className={styles.commentMarkdown__list}>{items}</ul>
}

const renderBlocks = (text: string, keyPrefix: string): React.ReactNode[] => {
    const lines = text.split('\n')
    const blocks: React.ReactNode[] = []
    let paragraph: string[] = []
    let index = 0

    const flushParagraph = () => {
        const value = paragraph.join('\n').replace(/^\n+|\n+$/g, '')
        if (value) {
            const key = `${keyPrefix}-p${blocks.length}`
            blocks.push(<p key={key} className={styles.commentMarkdown__paragraph}>{renderInline(value, key)}</p>)
        }
        paragraph = []
    }

    while (index < lines.length) {
        const line = lines[index]
        const key = `${keyPrefix}-${index}`

        if (FENCE_RE.test(line)) {
            const codeLines: string[] = []
            index += 1
            while (index < lines.length && !FENCE_RE.test(lines[index])) codeLines.push(lines[index++])
            index += 1
            flushParagraph()
            blocks.push(<pre key={key} className={styles.commentMarkdown__pre}><code>{codeLines.join('\n')}</code></pre>)
            continue
        }

        if (TABLE_ROW_RE.test(line) && TABLE_SEPARATOR_RE.test(lines[index + 1] || '')) {
            const rows: string[][] = [splitTableRow(line)]
            index += 2
            while (index < lines.length && TABLE_ROW_RE.test(lines[index])) rows.push(splitTableRow(lines[index++]))
            flushParagraph()
            const columnCount = Math.max(...rows.map((row) => row.length))
            const renderCells = (row: string[], rowIndex: number, Cell: 'th' | 'td') => (
                Array.from({ length: columnCount }, (_, cellIndex) => (
                    <Cell key={cellIndex}>{renderInline(row[cellIndex] || '', `${key}-${rowIndex}-${cellIndex}`)}</Cell>
                ))
            )
            blocks.push(
                <div key={key} className={styles.commentMarkdown__tableWrap}>
                    <table className={styles.commentMarkdown__table}>
                        <thead><tr>{renderCells(rows[0], 0, 'th')}</tr></thead>
                        <tbody>
                            {rows.slice(1).map((row, rowIndex) => <tr key={rowIndex}>{renderCells(row, rowIndex + 1, 'td')}</tr>)}
                        </tbody>
                    </table>
                </div>
            )
            continue
        }

        const heading = line.match(HEADING_RE)
        if (heading) {
            flushParagraph()
            const Heading = `h${Math.min(heading[1].length + 2, 6)}` as 'h3'
            blocks.push(<Heading key={key} className={styles.commentMarkdown__heading}>{renderInline(heading[2], key)}</Heading>)
            index += 1
            continue
        }

        if (QUOTE_RE.test(line)) {
            const quoteLines: string[] = []
            while (index < lines.length && QUOTE_RE.test(lines[index])) quoteLines.push(lines[index++].match(QUOTE_RE)?.[1] || '')
            flushParagraph()
            blocks.push(<blockquote key={key} className={styles.commentMarkdown__quote}>{renderBlocks(quoteLines.join('\n'), key)}</blockquote>)
            continue
        }

        if (HR_RE.test(line)) {
            flushParagraph()
            blocks.push(<hr key={key} className={styles.commentMarkdown__hr}/>)
            index += 1
            continue
        }

        if (LIST_RE.test(line)) {
            const listLines: string[] = []
            while (index < lines.length && LIST_RE.test(lines[index])) listLines.push(lines[index++])
            flushParagraph()
            parseList(listLines).forEach((block, blockIndex) => blocks.push(renderList(block, `${key}-${blockIndex}`)))
            continue
        }

        paragraph.push(line)
        index += 1
    }

    flushParagraph()
    return blocks
}

export default function CommentMarkdown({ text, className }: CommentMarkdownProps) {
    return (
        <div className={`${styles.commentMarkdown} ${className || ''}`}>
            {renderBlocks(text.replace(/\r\n?/g, '\n'), 'md')}
        </div>
    )
}
